import {useSafeAsyncCallback} from '@github-ui/use-safe-async-callback'
import {createContext, memo, type ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react'

import {ItemType} from '../../api/memex-items/item-type'
import {getColumnModel} from '../../hooks/tanstack-table/helpers/column-api'
import {getVisibleCells} from '../../hooks/tanstack-table/helpers/row-api'
import type {Cell, Row} from '../../hooks/tanstack-table/helpers/types'
import {useTableInstance} from '../../hooks/tanstack-table/providers/table-provider'
import {useBulkUpdateItems} from '../../hooks/use-bulk-update-items'
import type {ColumnModel} from '../../models/column-model'
import type {MemexItemModel} from '../../models/memex-item-model'
import {buildUpdateCopyingCell} from '../../state-providers/column-values/column-value'
import {HistoryResources} from '../../strings'
import {
  useAreCellsBulkSelected,
  useGetBulkSelectedRows,
  useTableCellBulkSelectionActions,
  useTableCellBulkSelectionData,
} from './table-cell-bulk-selection'
import type {TableDataType} from './table-data-type'
import {useCopilotAssigneeUpdate} from './use-copilot-assignee-update'

/**
 * Returns a callback to replace the contents of all selected cells with the contents of the first selected cell.
 */
export const useFillSelection = () => {
  const selectionState = useTableCellBulkSelectionData()
  const hasBulkSelection = useAreCellsBulkSelected()
  const getSelectedRows = useGetBulkSelectedRows()
  const {bulkUpdateSingleColumnValue: bulkUpdateItems} = useBulkUpdateItems()
  const handleCopilotAssigneeUpdate = useCopilotAssigneeUpdate(HistoryResources.fillCells)

  /**
   * @param direction Controls which cell is the copy source. `down` will always use the top cell. `as-selected` will use
   * the first selected cell.
   */
  return useCallback(
    async (direction: 'down' | 'as-selected') => {
      if (!hasBulkSelection || !selectionState.columnId) return

      const selectedRows = getSelectedRows()
      const [sourceRow, ...targetRows] =
        direction === 'as-selected' ? selectedRows : selectedRows.sort((a, b) => a.index - b.index)

      if (targetRows.length === 0) return

      const sourceCell = sourceRow
        ? getVisibleCells(sourceRow).find(cell => cell.column.id === selectionState.columnId)
        : undefined
      if (!sourceCell) return

      const update = buildUpdateCopyingCell(sourceCell)
      if (!update) return

      const itemsToFill = targetRows.map(row => row.original)
      const sourceCellRepoId = sourceCell.row.original.contentRepositoryId

      // Handle Copilot assignee updates with modal logic
      const wasHandledByCopilotModal = await handleCopilotAssigneeUpdate(itemsToFill, update, sourceCellRepoId)
      if (wasHandledByCopilotModal) {
        return
      }

      // Standard bulk update for non-Copilot cases or when feature is disabled
      bulkUpdateItems(itemsToFill, update, HistoryResources.fillCells, sourceCellRepoId)
      return
    },
    [bulkUpdateItems, getSelectedRows, hasBulkSelection, selectionState.columnId, handleCopilotAssigneeUpdate],
  )
}

interface DragToFillContext {
  isDragging: boolean
  onFillStart?: (columnId: string, rowIndex: number) => void
}

const DragToFillContext = createContext<DragToFillContext>({isDragging: false})

export const TableDragToFillProvider = memo(function TableDragToFillProvider({children}: {children: ReactNode}) {
  const [isDragging, setIsDragging] = useState<boolean>(false)

  const fillSelection = useFillSelection()

  const onFillEnd = useSafeAsyncCallback(() => {
    fillSelection('as-selected')
    setIsDragging(false)
  })

  useEffect(() => {
    if (isDragging) document.addEventListener('mouseup', onFillEnd)
    return () => document.removeEventListener('mouseup', onFillEnd)
  }, [isDragging, onFillEnd])

  const onFillStart = useCallback(() => setIsDragging(true), [])

  const bulkSelectActions = useTableCellBulkSelectionActions()
  if (!bulkSelectActions)
    throw new Error('`TableDragToFillProvider` requires `TableCellBulkSelectionProvider` as an ancestor')

  return (
    <DragToFillContext.Provider value={useMemo(() => ({isDragging, onFillStart}), [isDragging, onFillStart])}>
      {children}
    </DragToFillContext.Provider>
  )
})

export const useDragToFill = (cell: Pick<Cell<TableDataType>, 'column' | 'row'>) => {
  const {isDragging, onFillStart} = useContext(DragToFillContext)

  const selectionState = useTableCellBulkSelectionData()
  const orderedSelectedIds = Array.from(selectionState.rowIds)

  const isFillSourceCell =
    isDragging && selectionState.columnId === cell.column.id && orderedSelectedIds[0] === cell.row.original.id

  const isFillLastCell =
    isDragging && selectionState.columnId === cell.column.id && orderedSelectedIds.at(-1) === cell.row.original.id

  const onFillHandleMousedown = useCallback(
    (event: React.MouseEvent) => {
      // The row used for fill source is the first row put into the selectionState.rowIndexes Set.
      // If the user has a modifier key pressed, they could be keeping previously selected rows in the selection instead
      // of clearing it, which would cause the first row to be unexpectedly not the row where the selection started.
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (!(event.metaKey || event.ctrlKey || event.shiftKey)) {
        onFillStart?.(cell.column.id, cell.row.index)
      }
    },
    [cell, onFillStart],
  )

  const fillEmptyDown = useFillEmptyCellsDown(cell)
  const onFillHandleDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      fillEmptyDown()
    },
    [fillEmptyDown],
  )

  return onFillStart
    ? {
        isFillSourceCell,
        isFillLastCell,
        onFillHandleMousedown,
        onFillHandleDoubleClick,
      }
    : null
}

// By using row & columnModel here instead of cell, we can avoid having to iterate through each row's cells to find the cell in the column we need
const isEmptyCell = (row: Row<MemexItemModel>, columnModel: ColumnModel) => {
  const columnId = columnModel.id
  // Numeric columns use {value: number} so no need to check for zero
  return !row.original.columns[columnId]
}

export const useFillEmptyCellsDown = (cell: Pick<Cell<TableDataType>, 'column' | 'row'>) => {
  const table = useTableInstance()
  const allRows = table.getRowModel().flatRows
  const bulkSelectActions = useTableCellBulkSelectionActions()
  const fillSelection = useFillSelection()

  const safeFillSelection = useSafeAsyncCallback(fillSelection)

  return useCallback(() => {
    const sourceRow = cell.row
    const columnModel = getColumnModel(cell.column)
    if (!bulkSelectActions || !columnModel || isEmptyCell(sourceRow, columnModel)) return

    const followingRows = allRows.slice(sourceRow.index + 1)

    // We will fill all the empty cells up to the next filled one, the next redacted row, the end of the group, or the end of the table
    const indexAfterTargetInSlice = followingRows.findIndex(
      row =>
        row.original.contentType === ItemType.RedactedItem ||
        !isEmptyCell(row, columnModel) ||
        sourceRow.groupedValue !== row.groupedValue,
    )

    const targetIndex = indexAfterTargetInSlice === -1 ? allRows.length - 1 : indexAfterTargetInSlice + sourceRow.index
    if (targetIndex === sourceRow.index) return

    const targetRow = allRows[targetIndex]
    if (!targetRow) return
    const targetCell = getVisibleCells(targetRow).find(c => c.column.id === cell.column.id)
    if (!targetCell) return

    bulkSelectActions.select(cell, 'replace') // ensure this is the only cell selected and is the source for the range
    bulkSelectActions.selectRange(targetCell, 'replace')

    // Hack: fillSelection depends on selectionState, so we need it to be updated before we call it
    setTimeout(() => safeFillSelection('down'))
  }, [bulkSelectActions, cell, safeFillSelection, allRows])
}

/**
 * Returns `true` when the user is dragging to fill
 */
export const useIsDraggingToFill = () => {
  return useContext(DragToFillContext).isDragging
}

try{ DragToFillContext.displayName ||= 'DragToFillContext' } catch {}
try{ TableDragToFillProvider.displayName ||= 'TableDragToFillProvider' } catch {}